import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { TutorSlotType } from '@brighterly/lib-core-types/tutor/enums'

import { useListsStore } from '@shared/model'
import { getFirstQueryValue } from '@shared/lib/routes'

import { useDemoShiftControlStore } from './store'

export type ShiftFilterType = 'reservation-type'
  | 'slot-status'
  | 'sort-by'

const SORT_BY_OPTIONS: AppSelectOption[] = [
  { text: 'Slot status', value: 'status' },
  { text: 'Tutor name', value: 'tutor_name' },
]

interface ShiftFilterConfig {
  label: string
  queryKey: string
  getStoreValue: () => string | null
  setStoreValue: (value: string | null) => void
  getOptions: () => AppSelectOption[]
}

interface ShiftFilterReturn {
  label: string
  placeholder: string
  disabled: ComputedRef<boolean>
  options: ComputedRef<AppSelectOption[]>
  modelValue: WritableComputedRef<string | undefined>
}

export const buildShiftFilterConfig = (type: ShiftFilterType): ShiftFilterConfig => {
  const demoShiftControlStore = useDemoShiftControlStore()
  const listsStore = useListsStore()

  if (type === 'reservation-type') {
    return {
      label: 'Reservation type',
      queryKey: 'reservation_types',
      getStoreValue: () => demoShiftControlStore.getFilterReservationType,
      setStoreValue: (value) => demoShiftControlStore.setFilterReservationType(value),
      getOptions: () => listsStore.getTutorSlotTypeOptions.filter(option => option.value !== TutorSlotType.Paid),
    }
  }

  if (type === 'sort-by') {
    return {
      label: 'Sort by',
      queryKey: 'sort_by',
      getStoreValue: () => demoShiftControlStore.getFilterSortBy,
      setStoreValue: (value) => demoShiftControlStore.setFilterSortBy(value),
      getOptions: () => SORT_BY_OPTIONS,
    }
  }

  return {
    label: 'Slot status',
    queryKey: 'statuses',
    getStoreValue: () => demoShiftControlStore.getFilterSlotStatus,
    setStoreValue: (value) => demoShiftControlStore.setFilterSlotStatus(value),
    getOptions: () => listsStore.getTutorSlotDemoStatusOptions,
  }
}

export const useShiftFilter = (config: ShiftFilterConfig): ShiftFilterReturn => {
  const placeholder = 'Select here'

  const route = useRoute()
  const listsStore = useListsStore()
  const demoShiftControlStore = useDemoShiftControlStore()

  const initFromQuery = (): void => {
    const firstValue = getFirstQueryValue(route?.query?.[config.queryKey])
    if (!firstValue) return

    config.setStoreValue(firstValue)
  }

  initFromQuery()

  const disabled = computed<boolean>(() => {
    if (config.queryKey === 'sort_by') return demoShiftControlStore.isShiftControlBusy
    return !listsStore.isTutorSlotType || demoShiftControlStore.isShiftControlBusy
  })
  const options = computed<AppSelectOption[]>(() => config.getOptions())

  const modelValue = computed<string | undefined>({
    get: () => config.getStoreValue() ?? undefined,
    set: (value) => config.setStoreValue(value ?? null),
  })

  return {
    label: config.label,
    placeholder,
    disabled,
    options,
    modelValue,
  }
}
